/**
 * Storefront v7 — the facts strip under the stage and the closing slab.
 *
 * FactsStrip: three or four short cells. The merchant's own hero_points
 * (their words) come first; otherwise the configured fulfilment facts and
 * the catalogue counts. Fewer than two cells → nothing renders.
 *
 * Closing: the last word before the footer — the merchant's own punchline
 * when they wrote one (the platform's pass-flavoured default is guarded
 * upstream), else the brand name with a neutral line — and the same primary
 * action as the stage.
 */
import React from 'react';
import { ArrowRight, Clock, MapPin, Tag, Bike, Utensils, CalendarDays } from 'lucide-react';
import { OrderNowButton } from '../primitives';

const ICON = { hours: Clock, pickup: MapPin, delivery: Bike, prep: Clock, minimum: Tag, count: Utensils, point: CalendarDays };

/** cells: [{ kind, label, text }] — built by the page from hero_points or fulfilment facts. */
export const FactsStrip = ({ cells = [] }) => {
  const list = (Array.isArray(cells) ? cells : []).filter((c) => c && (c.label || c.text)).slice(0, 4);
  if (list.length < 2) return null;
  return (
    <section id="facts" className="s6-container mt-6 md:mt-8" data-testid="s6-facts-strip">
      <ul className="s6-card grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-4 px-5 py-4 md:px-7 md:py-5" style={{ gridTemplateColumns: `repeat(${Math.min(list.length, 4) > 2 ? 2 : list.length}, minmax(0, 1fr))` }}>
        {list.map((c, i) => {
          const Icon = ICON[c.kind] || CalendarDays;
          return (
            <li key={`${c.kind}-${i}`} className="flex items-start gap-3 min-w-0" data-testid={`s6-fact-${c.kind}`}>
              <span className="mt-0.5 flex-shrink-0 w-8 h-8 rounded-full grid place-items-center" style={{ background: 'var(--s6-tint-ink)', color: 'var(--s6-ink)' }} aria-hidden="true"><Icon className="w-4 h-4" /></span>
              <span className="min-w-0">
                {c.label && <span className="block text-[11px] font-semibold uppercase tracking-[0.12em]" style={{ color: 'var(--s6-ink-muted)' }}>{c.label}</span>}
                <span className="block text-sm font-semibold leading-snug" style={{ color: 'var(--s6-ink)' }}>{c.text}</span>
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

/** The page builds the cells: merchant words first, then only configured facts. */
export const buildFactCells = ({ heroPoints = [], fulfilment = [], countText = '' }) => {
  const points = (Array.isArray(heroPoints) ? heroPoints : []).map((p) => String(p || '').trim()).filter(Boolean).slice(0, 4);
  if (points.length >= 2) return points.map((text) => ({ kind: 'point', label: '', text }));
  const cells = (Array.isArray(fulfilment) ? fulfilment : []).filter((f) => f && f.kind !== 'delivery').map((f) => ({ kind: f.kind, label: f.label, text: f.text }));
  if (countText) cells.push({ kind: 'count', label: 'On the menu', text: countText });
  return cells.slice(0, 4);
};

export const Closing = ({ headline, emphasis, body, cta, onCta }) => (
  <section id="closing" className="s6-section s6-surface-dark s6-on-dark" data-testid="s6-closing">
    <div className="s6-container text-center">
      <h2 className="s6-display" data-testid="s6-closing-headline">
        {headline}
        {emphasis ? <><br /><span className="s6-emphasis">{emphasis}</span></> : null}
      </h2>
      {body && <p className="s6-lede mx-auto mt-4 opacity-85" data-testid="s6-closing-body">{body}</p>}
      {cta && (
        <div className="mt-8">
          <OrderNowButton onClick={onCta} data-testid="s6-closing-cta">{cta} <ArrowRight className="w-4 h-4" aria-hidden="true" /></OrderNowButton>
        </div>
      )}
    </div>
  </section>
);
